import React, { useState } from 'react';
import { Box, TextField, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import UrlField from './UrlField';
import { WriteButton, WriteIconButton } from '../../ui';

interface Props {
  label: string;
  description?: string;
  value: unknown;
  onChange: (v: Record<string, string>) => void;
}

const IconMapField: React.FC<Props> = ({ label, description, value, onChange }) => {
  const map: Record<string, string> = (value && typeof value === 'object' && !Array.isArray(value))
    ? (value as Record<string, string>)
    : {};
  const keys = Object.keys(map);
  const [newKey, setNewKey] = useState('');

  const updateKeyValue = (k: string, url: string) => {
    onChange({ ...map, [k]: url });
  };

  const renameKey = (oldKey: string, newName: string) => {
    if (!newName || newName === oldKey || map[newName] !== undefined) return;
    const next: Record<string, string> = {};
    for (const k of keys) {
      next[k === oldKey ? newName : k] = map[k];
    }
    onChange(next);
  };

  const deleteKey = (k: string) => {
    const next = { ...map };
    delete next[k];
    onChange(next);
  };

  const addKey = () => {
    const k = newKey.trim();
    if (!k || map[k] !== undefined) return;
    onChange({ ...map, [k]: '' });
    setNewKey('');
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#141417', mb: 0.5, fontFamily: 'Manrope, sans-serif' }}>
        {label}
      </Typography>
      {description && (
        <Typography sx={{ fontSize: 11, color: '#6F6F6F', mb: 1 }}>{description}</Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {keys.map((k) => (
          <Box key={k} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1, border: '1px solid #EFF0F0', borderRadius: '8px' }}>
            <TextField
              size="small"
              defaultValue={k}
              onBlur={(e) => renameKey(k, e.target.value.trim())}
              sx={{ width: 180, flexShrink: 0 }}
              InputProps={{ sx: { fontSize: 12, fontFamily: 'monospace', borderRadius: '6px' } }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <UrlField
                label=""
                value={map[k] || ''}
                onChange={(v: string) => updateKeyValue(k, v)}
                placeholder="https://app-static.{env}.sunnyrewards.com/..."
              />
            </Box>
            <Tooltip title={`Remove "${k}"`}>
              <WriteIconButton
                size="small"
                onClick={() => deleteKey(k)}
                sx={{ color: '#DC2626', mt: 0.5, '&:hover': { backgroundColor: '#FEE2E2' } }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </WriteIconButton>
            </Tooltip>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="New icon key (e.g. settings)"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKey(); } }}
          sx={{ width: 240 }}
          InputProps={{ sx: { fontSize: 12, fontFamily: 'monospace', borderRadius: '6px' } }}
        />
        <WriteButton
          size="small"
          variant="outlined"
          onClick={addKey}
          disabled={!newKey.trim() || map[newKey.trim()] !== undefined}
          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          sx={{ textTransform: 'none', fontSize: 12, borderRadius: '6px' }}
        >
          Add icon
        </WriteButton>
      </Box>
    </Box>
  );
};

export default IconMapField;
